import { formatDay, tt } from '@agos/shared'

/**
 * The Article 17 panel's state — FRONTEND.md §9.240, FB-399.
 *
 * **Three steps rather than a boolean**, because the panel has to be unable to offer the request and
 * the withdrawal at once. `idle` asks, `confirming` warns, `requested` acknowledges and offers the
 * way back.
 *
 * **The confirmation is not politeness.** `CustomerAccountService.requestDeletion` stamps
 * `deletion_requested_at` *and* sets both marketing opt-ins to false in the same transaction, so the
 * press has an effect the customer cannot see from the button that caused it. Legacy asks first and
 * this is that, ported.
 *
 * **And `requested` hides the request button on purpose.** The same service assigns
 * `OffsetDateTime.now()` unconditionally, so pressing it a second time — which is exactly what
 * somebody does when they cannot tell whether the first one landed — moves the date an administrator
 * acts on. Legacy hides it; the previous AGOS panel left it there with no acknowledgement of what a
 * second press would do.
 */

export type DeletionStep = 'idle' | 'confirming' | 'requested'

export function deletionStep(requestedAt: string | null | undefined, confirming: boolean): DeletionStep {
  // The server's answer wins over the local step: a request on file is a request on file, whatever
  // the screen was in the middle of.
  if (typeof requestedAt === 'string' && requestedAt.trim() !== '') return 'requested'
  return confirming ? 'confirming' : 'idle'
}

/**
 * *Žiadosť o zmazanie bola podaná 01. 08. 2026.* — legacy's sentence, with the shared formatter.
 *
 * The date is the point. The old line said only that *something* had been asked for, which leaves a
 * customer unable to tell a request they made this morning from one they made in June, and gives
 * them nothing to quote when they ask how it is going.
 */
export function deletionRequestedNote(requestedAt: string | null | undefined): string {
  const stamp = typeof requestedAt === 'string' ? requestedAt.trim() : ''
  if (stamp === '') return tt('Žiadosť o zmazanie účtu je podaná.')
  const when = new Date(stamp)
  // An unparseable timestamp is not worth printing as `Invalid Date`; the request is still real.
  if (Number.isNaN(when.getTime())) return tt('Žiadosť o zmazanie účtu je podaná.')
  return `${tt('Žiadosť o zmazanie bola podaná')} ${formatDay(when)}.`
}

/**
 * What the request does, said before it is made rather than after.
 *
 * **One literal, not a concatenation.** `tt` keys on the string it is handed, so a sentence assembled
 * with `+` would look up a key nothing has ever stored and could never be translated. The lint rule
 * that refuses a bare Slovak fragment is the same rule catching that.
 */
export function deletionConsequences(): string {
  return tt(
    'Marketingové notifikácie sa okamžite vypnú. Profil a údaje sa zmažú po overení administrátorom. Objednávková história zostane v účtovných záznamoch po zákonom požadovanú dobu.',
  )
}

/**
 * What withdrawing does **not** do.
 *
 * `cancelDeletionRequest` clears the timestamp and nothing else, so the two marketing switches stay
 * off. That is deliberate — re-subscribing somebody as a side effect of changing their mind would be
 * the worse defect — and it is said rather than left to be discovered.
 */
export function withdrawalNote(): string {
  return tt('Marketingové notifikácie zostanú vypnuté - zapnúť sa dajú vyššie v Notifikáciách.')
}
